import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Button, Table, Dropdown, Card, Spin, Empty, type TableColumnType, type MenuProps } from 'antd';
import { EllipsisOutlined, LoadingOutlined } from 'antd/icons';
import useWidthBreakpoint from 'vl-common/src/hooks/useWidthBreakpoint';
import useReactiveColumns from 'vl-common/src/hooks/useReactiveColumns';
import usePagedQuery from '@hooks/usePagedQuery';
import useColumnFilters from '@hooks/useColumnFilters';
import * as actions from '@actions';
import format from 'vl-common/src/lib/formatDate';
import Datestamp from 'vl-common/src/components/Datestamp';
import Pagination from '@components/UI/Table/Pagination';
import useIsRestricted from '@hooks/useIsRestricted';
import useAuth from '@vl-core/useAuth';
import { InfoModal, SuccessModal, confirmationModal } from 'vl-common/src/components/Modals';
import useFileDownloader from '@hooks/useFileDownloader';
import PreviewModal from 'vl-common/src/components/TimeLine/PreviewModal';
import LetterFilePreviewModal from 'vl-common/src/components/TimeLine/LetterFilePreviewModal';
import { getFileExtension } from 'vl-common/src/utils';
import { useFileEncryptionHintModal } from '@hooks/useFileEncryptionHintModal';

type Row = {
  letter_type: string;
  file_guid: string;
  description: string;
  patient_user_guid: string;
  delete_status: string;
  document_id: number;
  created_date: Date;
  published_to_patient: boolean;
  file_encrypted: boolean;
  document_type?: string;
  superseded_appt?: boolean;
  uploaded_by?: string;
  amendment?: boolean;
};

type FileType = {
  patientGuid: string;
  fileName: string;
  isFactsheet?: boolean;
  isImage?: boolean;
  uploadedBy?: string;
  createdDate?: string;
  supersededAppt?: boolean;
  amendment?: boolean;
  title?: string;
};

const usePreviousCaseId = (caseId?: number) => {
  const ref = useRef<number>(undefined);
  const prev = ref.current;

  useEffect(() => {
    ref.current = caseId;
  }, [caseId]);

  return prev;
};

const LetterDocuments = ({
  appId,
  caseId,
  pageSize,
  refresh,
  previewOnly = false,
  'data-testid': dataTestId = 'letter-documents-table'
}:
  | {
      appId: number;
      caseId?: number;
      refresh?: boolean;
      pageSize?: number;
      previewOnly?: boolean;
      'data-testid'?: string;
    }
  | {
      appId?: number;
      caseId: number;
      refresh?: number;
      pageSize?: number;
      previewOnly?: boolean;
      'data-testid'?: string;
    }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileType>({ patientGuid: '', fileName: '' });
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [fileId, setFileId] = useState<number>();
  const [removeSuccess, setRemoveSuccess] = useState(false);
  const { user } = useAuth();
  const [paginationCurrent, setPaginationCurrent] = useState(1);
  const currentBreakPoint = useWidthBreakpoint();
  const isRestricted = useIsRestricted();
  const previousCaseId = usePreviousCaseId(caseId);
  const { downloadFile, openFileInNewTab } = useFileDownloader();
  const fileEncryptionHintModal = useFileEncryptionHintModal();
  const isClinician = user?.user_type_code === 'CLINICIAN';

  const previewInNewTab = useCallback(
    async (val: Row) => {
      if (val.letter_type === 'FACTSHEET') {
        window.open(val.file_guid, '_blank');
        return;
      }

      await openFileInNewTab(val.patient_user_guid, val.file_guid);
    },
    [openFileInNewTab]
  );

  const publishToPatient = (val: Row): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      if (!user) resolve(false);

      confirmationModal({
        title: 'Publish to patient',

        content: (
          <>
            <p>
              {t('Are you sure you want to publish ')}
              <code>{val.file_guid}</code>,{t(' to the patient?')}'
            </p>

            <p>
              <Button
                onClick={() => {
                  setIsOpenPreviewModal(true);
                  setSelectedFile({
                    patientGuid: val.patient_user_guid,
                    fileName: val.file_guid,
                    isFactsheet: val.letter_type === 'FACTSHEET',
                    isImage: val.document_type?.toUpperCase() === 'IMAGE',
                    uploadedBy: val.uploaded_by,
                    createdDate: val.created_date ? String(val.created_date) : undefined,
                    supersededAppt: val.superseded_appt,
                    amendment: val.amendment,
                    title: getDocumentDisplayTitle(val)
                  });
                }}
                data-testid="view-document"
                size="small"
              >
                {t('Preview ')}
                {val.file_guid}
              </Button>
            </p>

            <p>{t('This action cannot be undone.')}</p>
          </>
        ),
        async onOk() {
          const result = await dispatch(
            actions.publishToPatient({ file_id: val.document_id, user_guid: user?.user_guid || '', appt_id: appId })
          );
          resolve(result?.ok);
        }
      });
    });

  const tableActionsMenu = useMemo(
    () => (val: Row) => {
      const fileExtention = getFileExtension(val.file_guid);
      const isDocOrText = ['doc', 'docx', 'txt'].includes(fileExtention.toLowerCase());

      type MenuOption = NonNullable<MenuProps['items']>[number] & { hidden?: boolean };

      const options: MenuOption[] = [
        {
          key: '0',
          label: 'Preview',
          disabled: !isClinician && isDocOrText,
          onClick: async () => {
            if (val?.file_encrypted) {
              await fileEncryptionHintModal();
            }

            // Fall back to download for Word documents and text files since they cannot be previewed in modal
            if (isDocOrText) {
              if (val.letter_type === 'FACTSHEET') {
                const link = document.createElement('a');
                link.href = val.file_guid;
                link.target = '_blank';
                link.download = `${val.description}.${fileExtention}`;
                link.click();
              } else {
                await downloadFile(val.patient_user_guid, val.file_guid);
              }
              return;
            }

            if (previewOnly) {
              await previewInNewTab(val);
              return;
            }

            setIsOpenPreviewModal(true);
            setSelectedFile({
              patientGuid: val.patient_user_guid,
              fileName: val.file_guid,
              isFactsheet: val.letter_type === 'FACTSHEET',
              isImage: val.document_type?.toUpperCase() === 'IMAGE',
              uploadedBy: val.uploaded_by,
              createdDate: val.created_date ? String(val.created_date) : undefined,
              supersededAppt: val.superseded_appt,
              amendment: val.amendment,
              title: getDocumentDisplayTitle(val)
            });
          }
        },
        {
          key: '1',
          label: 'Download',
          hidden: isClinician, // VLAXA-7311 - Disable file downloads for clinicians
          onClick: async () => {
            if (val?.file_encrypted) {
              await fileEncryptionHintModal();
            }
            if (val.letter_type === 'FACTSHEET') {
              const link = document.createElement('a');
              link.href = val.file_guid;
              link.target = '_blank';
              link.download = `${val.description}.pdf`;
              link.click();
            } else {
              downloadFile(val.patient_user_guid, val.file_guid).then();
            }
          }
        }
      ];

      if (val.delete_status === 'Y') {
        options.push({
          key: '2',
          label: 'Remove',
          hidden: previewOnly,
          onClick: () => {
            setFileId(val.document_id);
            setAlertModalVisible(true);
          }
        });
      }

      if (isClinician) {
        options.unshift({
          key: '3',
          label: 'Publish to patient',
          hidden: previewOnly,
          disabled: val.published_to_patient,
          onClick: () => publishToPatient(val).then(refetch)
        });
      }

      return options.filter((option) => !option?.hidden);
    },
    [dispatch, previewOnly, previewInNewTab, downloadFile, fileEncryptionHintModal]
  );

  const rawColumns: TableColumnType<Row>[] = useMemo(
    () => [
      {
        title: 'Document Title',
        dataIndex: 'description',
        key: 'description',
        render: (_: unknown, record: Row) => getDocumentDisplayTitle(record),
        onCell: () => ({ 'data-testid': 'document-title-cell' })
      },
      {
        title: 'Document Type',
        dataIndex: 'document_type',
        key: 'document_type',
        filterable: {
          exact: true,
          queryKey: 'doc_type'
        },
        responsive: ['sm'],
        onCell: () => ({ 'data-testid': 'document-type-cell' })
      },
      {
        title: 'Created Date',
        dataIndex: 'created_date',
        key: 'created_date',
        filterable: true,
        dateFilter: true,
        responsive: ['md'],
        render: (value: Date) => <Datestamp date={value} format="Date @ Time - 03/01/2022 @ 08:57" />,
        onCell: () => ({ 'data-testid': 'document-date-cell' })
      },
      { title: 'Uploaded By', dataIndex: 'uploaded_by', key: 'uploaded_by', responsive: ['lg'] },
      isClinician
        ? {
            title: 'Published to patient',
            dataIndex: 'published_to_patient',
            key: 'published_to_patient',
            render: (value: boolean) => <span>{value ? t('Yes') : t('No')}</span>
          }
        : {},
      {
        title: 'Actions',
        dataIndex: '',
        key: 'x',
        render: (val: Row) => (
          <Dropdown className="dropdown" trigger={['click']} menu={{ items: tableActionsMenu(val) }}>
            <Button data-testid="actions-menu" type="text" aria-label={t('Actions menu')} icon={<EllipsisOutlined />} />
          </Dropdown>
        ),
        onCell: () => ({ 'data-testid': 'document-actions-cell' })
      }
    ],
    [tableActionsMenu]
  );

  const { columns: tableColumn, query, handleTableChange } = useColumnFilters(rawColumns);

  const {
    isLoading: isLoadingDocumentList,
    page: documentList,
    lastPage,
    refetch
  } = usePagedQuery<Row>(
    !isRestricted && (appId || caseId)
      ? actions.getLetterFiles({
          ...(appId && { appt_id: appId }),
          ...(caseId && { case_id: caseId }),
          page: paginationCurrent,
          ...(pageSize && { rows: pageSize }),
          ...query
        })
      : () => [],
    ['getLetterFiles', appId, caseId, refresh],
    paginationCurrent,
    setPaginationCurrent,
    pageSize
  );

  const getDocumentDisplayTitle = useCallback((doc: Row | null) => {
    if (!doc) return '';

    if (doc?.document_type !== 'Letter') return doc?.description;

    if (doc.superseded_appt) return `${doc.description} - Superseded`;

    if (doc.amendment) return `${doc.description} - Current Version`;

    return doc.description;
  }, []);

  useEffect(() => {
    if (caseId !== previousCaseId && paginationCurrent > 1) {
      setPaginationCurrent(1);
    }
  }, [caseId, paginationCurrent, previousCaseId]);

  const [displayColumnList, hideColumnList] = useReactiveColumns(tableColumn, currentBreakPoint);

  const removeDoc = async () => {
    const request = {
      user_guid: user?.user_guid,
      file_id: fileId
    };
    await dispatch(actions.deleteLetterFile(request));
    refetch();
  };

  return (
    <>
      <Table<Row>
        data-testid={dataTestId}
        rowKey={(record) => record.document_id}
        columns={displayColumnList}
        dataSource={documentList}
        size="middle"
        pagination={false}
        locale={{
          emptyText: isLoadingDocumentList ? (
            <div style={{ height: '32px' }}>
              <Spin
                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                indicator={<LoadingOutlined style={{ fontSize: 26 }} spin />}
              />
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('You have no documents at the moment.')} />
          )
        }}
        expandable={{
          expandedRowRender: (record) => (
            <Card style={{ width: 'auto' }}>
              {' '}
              {hideColumnList.map((item, index) => (
                <p key={index + 0 /* NOSONAR */}>
                  <b>{String(item.title)} :</b>{' '}
                  {String(
                    item.dataIndex === 'created_date'
                      ? format(record.created_date, 'Date only (localised) - 03/02/2022')
                      : record[item.dataIndex as keyof Row]
                  )}
                </p>
              ))}
            </Card>
          ),
          rowExpandable: () => {
            return hideColumnList.length > 0;
          }
        }}
        onChange={handleTableChange}
      />

      <Pagination pageNumber={paginationCurrent} setPageNumber={setPaginationCurrent} isLastPage={lastPage} />

      {isOpenPreviewModal && (selectedFile.isFactsheet || selectedFile.isImage) && (
        <PreviewModal
          fileName={selectedFile.fileName}
          patientGuid={selectedFile.patientGuid}
          isFactsheet={selectedFile.isFactsheet}
          onClose={() => {
            setIsOpenPreviewModal(false);
            setSelectedFile({ patientGuid: '', fileName: '' });
          }}
        />
      )}

      {isOpenPreviewModal && !selectedFile.isFactsheet && !selectedFile.isImage && (
        <LetterFilePreviewModal
          open
          fileGuid={selectedFile.fileName}
          patientGuid={selectedFile.patientGuid}
          title={selectedFile.title}
          uploadedBy={selectedFile.uploadedBy}
          createdDate={selectedFile.createdDate}
          annotateCurrentLetter={selectedFile.amendment}
          onClose={() => {
            setIsOpenPreviewModal(false);
            setSelectedFile({ patientGuid: '', fileName: '' });
          }}
        />
      )}

      <InfoModal
        open={alertModalVisible}
        onOk={() => {
          removeDoc();
          setAlertModalVisible(false);
          setRemoveSuccess(true);
        }}
        onCancel={() => setAlertModalVisible(false)}
        title="Remove Document"
      >
        {t('Are you sure you wish to remove this document?')}
      </InfoModal>

      <SuccessModal
        open={removeSuccess}
        onCancel={() => setRemoveSuccess(false)}
        title="Document removed successfully"
        data-testid="dismiss"
      />
    </>
  );
};

export default LetterDocuments;
